import { getAccountStore } from '../account/accountStore.ts';
import { useProgressStore } from '../state/progressStore.ts';
import type { MetaClient, TrainingScore } from './types.ts';

/**
 * Training's records live on the device; with an account (not a guest) they are also told to the
 * service, which keeps the best and shows the board. Telling is best effort: the board is a
 * courtesy, a failure is not the player's to see, and the next time tries again.
 */

/** The progress service of a real account that is signed in, or null (no service, signed out, a guest). */
export function trainingMeta(): MetaClient | null {
  const { client, snapshot } = getAccountStore().getState();
  if (!client || snapshot.status !== 'signedIn' || snapshot.needsSecondFactor) return null;
  if (snapshot.user?.anonymous ?? true) return null;
  return client.meta();
}

/** The records of this device, as the service takes them. */
export function localTrainingScores(only?: { puzzle: string; level: number }): TrainingScore[] {
  const records = useProgressStore.getState().progress.training;
  return Object.entries(records).flatMap(([key, record]) => {
    const [puzzle = '', level = ''] = key.split(':');
    const n = Number(level);
    if (!puzzle || !Number.isInteger(n)) return [];
    if (only && (only.puzzle !== puzzle || only.level !== n)) return [];
    return [{ puzzle, level: n, ms: record.bestMs, stars: record.stars }];
  });
}

/** The account the records were last told for, so that a visit to Training tells them once. */
let toldFor: string | null = null;

/** Tells the service every record of this device (once for each account, per page load). */
export async function syncTrainingRecords(): Promise<void> {
  const meta = trainingMeta();
  const user = getAccountStore().getState().snapshot.user?.id ?? null;
  if (!meta || !user || toldFor === user) return;
  const scores = localTrainingScores();
  if (scores.length === 0) return;
  const result = await meta.submitTraining(scores);
  if (result.ok) toldFor = user;
}

/** Tells the service the record of one puzzle and level, as it is now on this device. */
export async function submitTrainingRecord(puzzle: string, level: number): Promise<void> {
  const meta = trainingMeta();
  if (!meta) return;
  const scores = localTrainingScores({ puzzle, level });
  if (scores.length > 0) await meta.submitTraining(scores);
}

/** For tests: forgets what was told. */
export function resetTrainingSync(): void {
  toldFor = null;
}
